import type { SocialImportPlace } from '@/utils/inspoImportBuilder';
import { usStateNameFromCode } from '@/components/MyTravel/myTravelTypes';

/** Matches ", XX ZIP" — the standard US postal format. Captures the 2-letter code. */
const US_STATE_CODE_REGEX = /,\s*([A-Z]{2})\s+\d{5}(?:-\d{4})?/;

/** Matches "XX, USA" as a looser fallback. Captures the 2-letter code. */
const US_STATE_CODE_TRAILING_REGEX = /,\s*([A-Z]{2}),\s*(?:USA|United States)\b/i;

/**
 * Extract a canonical US state name from a place's formatted address string.
 * Only handles standard US postal formats. Returns null for non-matches.
 */
export function extractUsStateFromAddress(address: string | null | undefined): string | null {
  if (!address) return null;
  const primary = US_STATE_CODE_REGEX.exec(address);
  if (primary) {
    const name = usStateNameFromCode(primary[1]);
    if (name) return name;
  }
  const fallback = US_STATE_CODE_TRAILING_REGEX.exec(address);
  if (fallback) {
    const name = usStateNameFromCode(fallback[1]);
    if (name) return name;
  }
  return null;
}

/**
 * Returns the US state appearing most often across the given places. Only
 * places whose `country` is "United States" are considered. Ties are broken by
 * first-selected. Returns null when no place is a US place, or when no US
 * place has a parseable state.
 */
export function pickDominantState(
  places: ReadonlyArray<Pick<SocialImportPlace, 'country' | 'address'>>,
): string | null {
  const counts = new Map<string, { count: number; firstIndex: number }>();
  for (let i = 0; i < places.length; i += 1) {
    const place = places[i];
    const country = place?.country?.trim();
    if (country !== 'United States') continue;
    const state = extractUsStateFromAddress(place.address);
    if (!state) continue;
    const existing = counts.get(state);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(state, { count: 1, firstIndex: i });
    }
  }
  if (counts.size === 0) return null;

  let winner: string | null = null;
  let winnerCount = -1;
  let winnerFirstIndex = Number.POSITIVE_INFINITY;
  for (const [state, { count, firstIndex }] of counts) {
    if (count > winnerCount || (count === winnerCount && firstIndex < winnerFirstIndex)) {
      winner = state;
      winnerCount = count;
      winnerFirstIndex = firstIndex;
    }
  }
  return winner;
}
